import {
  ApiProperty,
  ApiPropertyOptional,
  IntersectionType,
} from '@nestjs/swagger';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import { ContractorInterface } from '../interfaces/contractor.interface';
import { ArchivableEntityDto } from '../../common/dto/archivable-entity.dto';
import { JobPhaseDto } from '../../job-phase/dto/job-phase.dto';
import { JobPhaseContractorHistoryDto } from '../../job-phase-contractor-history/dto/job-phase-contractor-history.dto';
import {
  IsBoolean,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';
import { Exclude, Expose, Transform, Type } from 'class-transformer';
import { ContractorRegionDto } from '../../contractor-region/dto/contractor-region.dto';
import {
  CONTRACTOR_NAME_MIN_LENGTH,
  CONTRACTOR_NAME_MAX_LENGTH,
  CONTRACTOR_CORPORATION_MIN_LENGTH,
  CONTRACTOR_CORPORATION_MAX_LENGTH,
  CONTRACTOR_DESCRIPTION_MIN_LENGTH,
  CONTRACTOR_DESCRIPTION_MAX_LENGTH,
  CONTRACTOR_PAYMENT_RATE_MIN,
  CONTRACTOR_PAYMENT_RATE_MAX,
  CONTRACTOR_NOTES_MAX_LENGTH,
  CONTRACTOR_NOTES_MIN_LENGTH,
} from '../constants/contractor-validation.constants';
import { ContractorPaymentTypeEnum } from '../enums/contractor-payment-type.enum';
import { ContractorUserDto } from '../../contractor-user/dto/contractor-user.dto';
import { ContractorUserInterface } from '../../contractor-user/interfaces/contractor-user.interface';

@Exclude()
export class ContractorDto
  extends IntersectionType(CommonEntityDto, ArchivableEntityDto)
  implements ContractorInterface
{
  @ApiProperty({
    title: 'Name',
    description: 'The name of the Contractor',
    minLength: CONTRACTOR_NAME_MIN_LENGTH,
    maxLength: CONTRACTOR_NAME_MAX_LENGTH,
  })
  @IsString()
  @MinLength(CONTRACTOR_NAME_MIN_LENGTH)
  @MaxLength(CONTRACTOR_NAME_MAX_LENGTH)
  @Expose()
  name!: string;

  @ApiProperty({
    title: 'Short Name',
    description: 'The "short name" of the Contractor',
    minLength: CONTRACTOR_CORPORATION_MIN_LENGTH,
    maxLength: CONTRACTOR_CORPORATION_MAX_LENGTH,
  })
  @IsString()
  @MinLength(CONTRACTOR_CORPORATION_MIN_LENGTH)
  @MaxLength(CONTRACTOR_CORPORATION_MAX_LENGTH)
  @Expose()
  corporation!: string;

  @ApiProperty({
    title: 'Description',
    description: 'The description of the Contractor',
    minLength: CONTRACTOR_DESCRIPTION_MIN_LENGTH,
    maxLength: CONTRACTOR_DESCRIPTION_MAX_LENGTH,
  })
  @IsString()
  @MinLength(CONTRACTOR_DESCRIPTION_MIN_LENGTH)
  @MaxLength(CONTRACTOR_DESCRIPTION_MAX_LENGTH)
  @Expose()
  description!: string;

  @ApiProperty({
    title: 'Payment Type',
    description: 'The payment type.',
    enum: ContractorPaymentTypeEnum,
  })
  @IsEnum(ContractorPaymentTypeEnum)
  @Expose()
  paymentType!: ContractorPaymentTypeEnum;

  @ApiProperty({
    title: 'Payment Rate',
    description: 'The payment rate (in dollars)',
    minimum: CONTRACTOR_PAYMENT_RATE_MIN,
    maximum: CONTRACTOR_PAYMENT_RATE_MAX,
  })
  @Transform(
    (value) => (typeof value === 'string' ? parseFloat(value) : value),
    {
      toClassOnly: true,
    },
  )
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(CONTRACTOR_PAYMENT_RATE_MIN)
  @Max(CONTRACTOR_PAYMENT_RATE_MAX)
  @Expose()
  paymentRate!: number;

  @ApiProperty({
    type: 'string',
    nullable: true,
    title: 'Notes',
    description: 'Notes related to this contractor',
    minLength: CONTRACTOR_NOTES_MIN_LENGTH,
    maxLength: CONTRACTOR_NOTES_MAX_LENGTH,
  })
  @IsString()
  @MinLength(CONTRACTOR_NOTES_MIN_LENGTH)
  @MaxLength(CONTRACTOR_NOTES_MAX_LENGTH)
  @Expose()
  notes!: string;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Hanger Available?',
    description: 'True if contractor has at least one hanger available',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  hangerAvailable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Finisher Available?',
    description: 'True if contractor has at least one finisher available',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  finisherAvailable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Specialist Available?',
    description: 'True if contractor has at least one specialist available',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  specialistAvailable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: true,
    title: 'Active?',
    description: 'True if contractor is active',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  active!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Suspended?',
    description: 'True if contractor is suspended',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  suspended!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Can Perform High Ceiling Tasks?',
    description: 'True if contractor can perform high ceiling tasks',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  canPerformHighCeilingTasks!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Sprayer Available?',
    description: 'True if contractor has at least one sprayer available',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  sprayerAvailable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Sprayer Available?',
    description: 'True if contractor has at least one sprayer available',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  paintAvailable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Sprayer Available?',
    description: 'True if contractor has at least one sprayer available',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  trimAvailable!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: false,
    title: 'Bumper Available?',
    description: 'True if contractor has at least one bumper available',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  bumperAvailable!: boolean;

  @ApiProperty({
    title: 'Hanger Capacity',
    description: 'The number of hangers available for this contractor',
    nullable: true,
    default: null,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  hangerCapacity!: number | null;

  @ApiProperty({
    title: 'Finisher Capacity',
    description: 'The number of finishers available for this contractor',
    nullable: true,
    default: null,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  finisherCapacity!: number | null;

  @ApiProperty({
    title: 'Sprayer Capacity',
    description: 'The number of sprayers available for this contractor',
    nullable: true,
    default: null,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  sprayerCapacity!: number | null;

  @ApiProperty({
    title: 'Specialist Capacity',
    description: 'The number of specialists available for this contractor',
    nullable: true,
    default: null,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  specialistCapacity!: number | null;

  @ApiProperty({
    title: 'Paint Capacity',
    description: 'The number of painters available for this contractor',
    nullable: true,
    default: null,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  paintCapacity!: number | null;

  @ApiProperty({
    title: 'Trim Capacity',
    description: 'The number of trimmers available for this contractor',
    nullable: true,
    default: null,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  trimCapacity!: number | null;

  @ApiProperty({
    title: 'Bumper Capacity',
    description: 'The number of bumpers available for this contractor',
    nullable: true,
    default: null,
  })
  @IsOptional()
  @IsNumber()
  @Expose()
  bumperCapacity!: number | null;

  @ApiProperty({
    title: 'Bumper Payment Type',
    description:
      'The bumper payment type. ' +
      'Only required if bumper available is true.',
    enum: ContractorPaymentTypeEnum,
  })
  @IsEnum(ContractorPaymentTypeEnum)
  @Expose()
  bumperPaymentType!: ContractorPaymentTypeEnum;

  @ApiProperty({
    title: 'Bumper Payment Rate',
    description:
      'The bumper payment rate (in dollars). ' +
      'Only required if bumper available is true.',
    minimum: CONTRACTOR_PAYMENT_RATE_MIN,
    maximum: CONTRACTOR_PAYMENT_RATE_MAX,
  })
  @Transform(
    (value) => (typeof value === 'string' ? parseFloat(value) : value),
    {
      toClassOnly: true,
    },
  )
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(CONTRACTOR_PAYMENT_RATE_MIN)
  @Max(CONTRACTOR_PAYMENT_RATE_MAX)
  @Expose()
  bumperPaymentRate!: number;

  @ApiPropertyOptional({
    type: () => [ContractorRegionDto],
    title: 'Associated Contractor Regions',
    description: 'The regions associated with this contractor',
  })
  @Expose()
  contractorRegions?: ContractorRegionDto[];

  @ApiPropertyOptional({
    type: () => [JobPhaseDto],
    title: 'Associated Job Phases',
    description: 'The job phases associated with this contractor',
  })
  @Expose()
  jobPhases?: JobPhaseDto[];

  @ApiPropertyOptional({
    type: () => [JobPhaseDto],
    title: 'Associated Initial Job Phases',
    description:
      'The job phases associated with this contractor where they were the initial contractor',
  })
  @Expose()
  jobPhasesInitial?: JobPhaseDto[];

  @ApiPropertyOptional({
    type: () => [JobPhaseContractorHistoryDto],
    title: 'Associated Job Phase Contractor Histories',
    description:
      'The job phases contractor histories associated with this contractor',
  })
  @Expose()
  jobPhaseContractorHistories?: JobPhaseContractorHistoryDto[];

  @ApiPropertyOptional({
    type: () => [ContractorUserDto],
    title: 'Associated Contractor Users',
    description: 'Contractor Users associated with this contractor',
  })
  @Expose()
  contractorUsers?: ContractorUserInterface[];
}
